import React, { FC, useCallback } from "react";
import { ActionsGroup, Dialog, DialogActions, FlatButton } from "@evergis/ui";
import { useDispatch } from "react-redux";
import { FeatureDc } from "@evergis/api";
import { useTranslation } from "react-i18next";

import { useKey } from "../../../hooks/useKey";
import { useFeature } from "../../../hooks/feature/useFeature";
import { useFeatureToShow } from "../../../modules/FeatureSelector/hooks/useFeatureToShow";
import { useDialogReset } from "../../../hooks/useDialogs";
import { DeleteFeatureButton, DialogContent } from "./styled";
import { useDeleteFeature } from "../../../hooks/feature/useDeleteFeature";
import { useNotification } from "../../../hooks/notifications/useNotification";
import { addNotification } from "../../../ducks/notifications/notifications";
import { resetFeatureToShow } from "../../../ducks/showFeature/showFeature";
import { useFeatureCard } from "../../../modules/FeatureCard/hooks/useFeatureCard";

export const DeleteFeature: FC = () => {
  const { t } = useTranslation();
  const { feature: featureToDelete } = useFeatureToShow();
  const { featureName, featureLayer, featureTable } = useFeature(featureToDelete, "");
  const closeDialog = useDialogReset();
  const dispatch = useDispatch();
  const [deleteFeature, { error }] = useDeleteFeature(featureLayer, featureTable);
  const [, { closeCard }] = useFeatureCard();

  useNotification(error, {
    title: t("deleteFeature.deleteError", { ns: "dialogs" }),
    error: true,
  });

  const onDelete = useCallback(async () => {
    await deleteFeature(featureToDelete as FeatureDc);

    closeDialog();
    closeCard();

    dispatch(resetFeatureToShow());

    dispatch(
      addNotification({
        title: t("deleteFeature.deleteSuccess", { ns: "dialogs" }),
        success: true,
      }),
    );
  }, [closeCard, closeDialog, deleteFeature, dispatch, featureToDelete, t]);

  useKey({
    Enter: onDelete,
    Escape: closeDialog,
  });

  if (!featureToDelete) {
    closeDialog();

    return null;
  }

  return (
    <Dialog isOpen onCloseRequest={closeDialog} modal maxWidth={"27.6rem"} minHeight={"0"}>
      <DialogContent>
        {t("deleteFeature.deleteFeature", { ns: "dialogs" })} {featureName}?
      </DialogContent>
      <DialogActions>
        <ActionsGroup>
          <DeleteFeatureButton primary onClick={onDelete}>
            {t("actions.delete", { ns: "common" })}
          </DeleteFeatureButton>
          <FlatButton onClick={closeDialog}>{t("actions.cancel", { ns: "common" })}</FlatButton>
        </ActionsGroup>
      </DialogActions>
    </Dialog>
  );
};
